import { useState } from "react";
import { motion } from "framer-motion";
import {
  Sparkles, Palette, Gavel, Target, Megaphone,
  ChevronRight, Check, X,
} from "lucide-react";

const FAN_STEPS = [
  {
    id: "welcome",
    icon: Sparkles,
    color: "#FF6B35",
    title: "Bienvenue sur ReallyFan !",
    description: "Le reseau social ou tu peux soutenir tes artistes preferes, miser sur leur succes et remporter des lots exclusifs aux encheres.",
  },
  {
    id: "follow",
    icon: Sparkles,
    color: "#7000FF",
    title: "Suis tes artistes",
    description: "Explore la page Decouvrir pour trouver des artistes et commence a les suivre. Tu recevras leurs publications et leurs nouvelles encheres.",
    action: "discover",
    actionLabel: "Explorer",
  },
  {
    id: "bet",
    icon: Sparkles,
    color: "#FF6B35",
    title: "Mise sur leur succes",
    description: "Chaque artiste a un score. Mise tes credits quand le score est bas, et gagne quand il monte ! C'est la bourse de la musique.",
  },
  {
    id: "interact",
    icon: Sparkles,
    color: "#FF8FA3",
    title: "Interagis et gagne",
    description: "Like, commente, participe aux defis, complete les streaks quotidiens... chaque action te rapporte des XP et des credits !",
  },
  {
    id: "rewards",
    icon: Gavel,
    color: "#FFD700",
    title: "Remporte des encheres",
    description: "Les artistes mettent des lots exclusifs aux encheres : dedicaces, merch, rencontres... Le dernier encherisseur remporte le lot !",
    action: "rewards",
    actionLabel: "Voir les encheres",
  },
];

const ARTIST_STEPS = [
  {
    id: "welcome",
    icon: Sparkles,
    color: "#FF6B35",
    title: "Bienvenue sur ReallyFan !",
    description: "Ton espace artiste est pret. Ici tu peux engager ta communaute de fans, proposer des lots exclusifs aux encheres et lancer des defis.",
  },
  {
    id: "profile",
    icon: Palette,
    color: "#7000FF",
    title: "Personnalise ton profil",
    description: "Ajoute ton avatar, ta banniere et ta bio pour que les fans te trouvent facilement. Un profil complet attire plus de followers.",
    action: "dashboard",
    actionLabel: "Mon dashboard",
  },
  {
    id: "auction",
    icon: Gavel,
    color: "#FF6B35",
    title: "Cree ta premiere enchere",
    description: "Propose un lot aux encheres : dedicace, merch, rencontre... Ajoute une photo, fixe le prix de depart et laisse les fans encherir !",
    action: "dashboard",
    actionLabel: "Creer une enchere",
  },
  {
    id: "challenge",
    icon: Target,
    color: "#FF8FA3",
    title: "Lance des defis",
    description: "Les defis sont un excellent moyen d'interagir avec ta communaute. Cree des defis et recompense les fans les plus actifs.",
  },
  {
    id: "engage",
    icon: Megaphone,
    color: "#FFD700",
    title: "Publie et engage",
    description: "Partage ton actualite, tes coulisses, tes nouveautes. Chaque post est visible par toute la communaute, sans algorithme.",
    action: "feed",
    actionLabel: "Voir le fil",
  },
];

export const OnboardingOverlay = ({ onComplete, role = "fan" }) => {
  const [step, setStep] = useState(0);
  const STEPS = role === "artist" ? ARTIST_STEPS : FAN_STEPS;
  const currentStep = STEPS[step];
  const isLast = step === STEPS.length - 1;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-background/95 backdrop-blur-xl flex items-center justify-center p-6"
      data-testid="onboarding-overlay"
    >
      {/* Close X always visible */}
      <button
        onClick={onComplete}
        className="absolute top-5 right-5 w-10 h-10 rounded-full bg-card/80 border border-border backdrop-blur-sm flex items-center justify-center hover:scale-105 active:scale-95 transition-transform z-10"
        data-testid="onboarding-close-btn"
        aria-label="Fermer le tutoriel"
      >
        <X className="w-4 h-4 text-foreground" />
      </button>

      <motion.div
        key={step}
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -30, scale: 0.95 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-sm"
      >
        {/* Step indicator */}
        <div className="flex gap-1.5 justify-center mb-8">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i <= step ? "w-8 bg-[#FF6B35]" : "w-4 bg-foreground/10"
              }`}
            />
          ))}
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div
            className="w-20 h-20 rounded-3xl flex items-center justify-center"
            style={{ backgroundColor: `${currentStep.color}15` }}
          >
            <currentStep.icon
              className="w-10 h-10"
              style={{ color: currentStep.color }}
            />
          </div>
        </div>

        {/* Content */}
        <h2 className="font-heading font-extrabold text-2xl text-foreground text-center mb-3">
          {currentStep.title}
        </h2>
        <p className="text-sm text-muted-foreground text-center leading-relaxed mb-8 px-2">
          {currentStep.description}
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={() => {
              if (isLast) {
                onComplete();
              } else {
                setStep(step + 1);
              }
            }}
            className="w-full bg-gradient-to-r from-[#FF6B35] to-[#FF8FA3] text-white font-bold py-3.5 rounded-full shadow-[0_0_20px_rgba(255,107,53,0.3)] hover:scale-[1.02] active:scale-95 transition-transform flex items-center justify-center gap-2"
            data-testid="onboarding-next-btn"
          >
            {isLast ? (
              <>
                <Check className="w-4 h-4" /> C'est parti !
              </>
            ) : (
              <>
                Suivant <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>

          {!isLast && (
            <button
              onClick={onComplete}
              className="text-xs text-muted-foreground hover:text-[#AFA8BA] transition-colors text-center py-2"
              data-testid="onboarding-skip-btn"
            >
              Passer le tutoriel
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};
